// Guard Web feature flags — Nick's rule: everything on the web surface sits behind a flag, so a
// tenant (or a single user) can be steered to the native app or have a capture method switched off
// without a deploy. The server resolves user-over-account precedence and returns one flat JSON
// object; this module only parses it defensively and remembers the last server verdict.
//
// Stance for the pilot: the client gate is UX. The authoritative block lives server-side (Codex's
// lane — sync_scans already knows the scan's surface), so a flipped flag here is honest steering,
// not the security boundary.

export interface GuardWebFlags {
  /** Kill switch for the whole web surface — false renders the "use the mobile app" screen. */
  webAccess: boolean;
  qrCapture: boolean;
  nfcCapture: boolean;
  manualCapture: boolean;
  fieldStatus: boolean;
  /** Mobile-style duty home (route progress + checkpoint list). Ships dark until built + enabled. */
  dutyHome: boolean;
  /** Product analytics (PostHog). Also requires a runtime-injected key — no key, no SDK load. */
  analytics: boolean;
  /** Timeclock self-service (clock in/out, breaks). Ships dark until enabled per account/user. */
  timeclock: boolean;
  /** My Shifts schedule list + acknowledge/decline responses. Ships dark until enabled. */
  myShifts: boolean;
  /** Browser notification registration surface. Ships dark until the web client is intentionally wired. */
  notifications: boolean;
}

/** Absent server data must look exactly like today's app: everything shipped is on. */
export const DEFAULT_FLAGS: GuardWebFlags = {
  webAccess: true,
  qrCapture: true,
  nfcCapture: true,
  manualCapture: true,
  fieldStatus: true,
  dutyHome: false,
  analytics: true,
  timeclock: false,
  myShifts: false,
  notifications: false,
};

/** Server payload keys (snake_case jsonb) → client flag names. */
const SERVER_KEYS: ReadonlyArray<readonly [string, keyof GuardWebFlags]> = [
  ["web_access", "webAccess"],
  ["web_qr", "qrCapture"],
  ["web_nfc", "nfcCapture"],
  ["web_manual", "manualCapture"],
  ["web_field_status", "fieldStatus"],
  ["web_duty_home", "dutyHome"],
  ["web_analytics", "analytics"],
  ["web_timeclock", "timeclock"],
  ["web_my_shifts", "myShifts"],
  ["web_notifications", "notifications"],
];

/** Parse a server flag payload. Anything malformed degrades to the defaults, key by key. */
export function parseGuardWebFlags(raw: unknown): GuardWebFlags {
  const flags = { ...DEFAULT_FLAGS };
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return flags;
  }
  const record = raw as Record<string, unknown>;
  for (const [serverKey, clientKey] of SERVER_KEYS) {
    const value = record[serverKey];
    if (typeof value === "boolean") {
      flags[clientKey] = value;
    }
  }
  return flags;
}

export const LAST_GOOD_FLAGS_KEY_PREFIX = "secops.guard.lastFlags";

/** A cached verdict belongs to ONE account + signed-in user. Keying the cache this way is what
 *  stops a guard (or a second account on a shared browser) inheriting someone else's allow/deny. */
export interface FlagScope {
  accountId: string;
  userId: string;
}

export function lastGoodFlagsKey(scope: FlagScope): string {
  return `${LAST_GOOD_FLAGS_KEY_PREFIX}.${scope.accountId}.${scope.userId}`;
}

interface StringStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Remember the raw server payload so an offline PWA cold start keeps the last SCOPED verdict. */
export function persistLastGoodFlags(storage: StringStorageLike, scope: FlagScope, rawPayload: unknown): void {
  try {
    storage.setItem(lastGoodFlagsKey(scope), JSON.stringify(rawPayload ?? null));
  } catch {
    // Private mode / quota — flags simply won't survive a cold start.
  }
}

/** Load the remembered verdict for this exact account+user; null when absent or corrupt. */
export function loadLastGoodFlags(storage: StringStorageLike, scope: FlagScope): GuardWebFlags | null {
  try {
    const stored = storage.getItem(lastGoodFlagsKey(scope));
    if (stored === null) {
      return null;
    }
    return parseGuardWebFlags(JSON.parse(stored));
  } catch {
    return null;
  }
}
